import { ShieldExclamationIcon } from "@heroicons/react/24/outline";
import React, { useState, useRef, useEffect } from "react";
import { classNames } from "src/helpers/classNames";

const TextArea = ({
  rows = 3,
  label,
  name,
  disabled = false,
  inputRef = null,
  value = "",
  error = false,
  type = "text",
  styles = {},
  inputClassNames,
  labelClassNames = "",
  onChange = () => {},
  onBlur = () => {},
  placeholder = "",
  inline = false,
  wrapperClass,
  required = false,
  maxLength = null,
  errotText,
  inputInlineClasses = "",
  autoSize = false,
}) => {
  const [touched, setTouched] = useState(false);
  const [internalError, setInternalError] = useState(false);
  const textareaRef = useRef(null);

  const adjustHeight = () => {
    const ref = inputRef || textareaRef;
    if (ref.current && autoSize) {
      ref.current.style.height = "auto";
      ref.current.style.height = ref.current.scrollHeight + "px";
    }
  };

  useEffect(() => {
    adjustHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, autoSize]);

  const labelClasses = classNames(labelClassNames, "flex text-sm font-medium mb-1", !inline && disabled && "cursor-not-allowed", inline && disabled && "bg-transparent");
  const inputClasses = classNames(
    "break-all min-h-[80px] block w-full sm:text-sm border-gray-300 rounded-md shadow-sm focus:ring-0 focus:border-highlightColor",
    error && errotText && "border-red-300",
    disabled ? "text-gray-400" : "text-gray-700",
    !inline && disabled && "cursor-not-allowed",
    inline && disabled && "bg-transparent",
    inputInlineClasses,
  );

  const handleChange = (e) => {
    onChange(e);
    adjustHeight();
    if (required && touched) {
      setInternalError(!e.target.value.trim());
    }
  };

  const shouldShowError = required && touched && internalError;

  const handleBlur = (e) => {
    setTouched(true);
    onBlur(e);
    if (required) {
      setInternalError(!e.target.value.trim());
    }
  };

  return (
    <div className="w-full space-y-2">
      <div className={classNames(inline ? "relative h-full space-y-2 overflow-hidden rounded-md border px-0 pt-2 focus-within:border-highlightColor" : "relative h-full", inline && disabled ? "border-gray-100 bg-gray-50" : "border-gray-300 shadow-sm", wrapperClass)}>
        {label && (
          <div className="flex justify-between">
            <label
              htmlFor={name}
              className={inline ? classNames("m-0 flex pl-3 text-xs font-semibold uppercase leading-none text-gray-400 md:pl-2 md:text-[10px] 2xl:pl-3", labelClasses) : labelClasses}>
              {label} {error && <span className="pl-1 text-base font-semibold !leading-3 text-red-500">*</span>}
            </label>
            {maxLength && (
              <div className="m-0 pr-3 text-[11px] font-semibold uppercase leading-none text-gray-400 md:pr-2 md:text-[10px] 2xl:pr-3">
                ( {value.length} / {maxLength} ) characters
              </div>
            )}
          </div>
        )}
        <textarea
          ref={inputRef || textareaRef}
          style={{ ...styles, ...(autoSize && { resize: "none" }) }}
          disabled={disabled}
          className={classNames(inputClassNames ? inputClassNames : inputClasses, type === "code" ? "font-mono" : "", inline ? "h-28 rounded-none border-none bg-transparent py-0 shadow-none focus:border-none focus:ring-0" : "")}
          placeholder={placeholder}
          rows={autoSize ? undefined : rows}
          type={type}
          name={name}
          onBlur={handleBlur}
          value={value}
          maxLength={maxLength}
          onChange={handleChange}
        />
      </div>
      {shouldShowError && (
        <div className="animate-fade-in mt-.5 flex items-center text-xs leading-none text-highlightColor">
          <ShieldExclamationIcon className="mr-2 h-4 w-4" />
          {label ? `${label} is required.` : "This field is required."}
        </div>
      )}
    </div>
  );
};

export default TextArea;
